import { motion } from "framer-motion";
import { CheckCircle2, Eye, Zap, Shield, Layers, Target } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";
import { useScrollAnimations } from "@/components/GSAPScrollAnimations";

const MinimalistLogoDesignGuide = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainer = useScrollAnimations();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Minimalist Logo Design Guide",
      "description": "Create powerful minimalist logos that build brand recognition. Learn the principles of minimalist design and see examples from successful brands.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/minimalist-logo-design-guide"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main ref={scrollContainer} className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Minimalist Logo Design Guide
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Learn how to create powerful, memorable logos through minimalist design principles. Discover why less is more in logo design and how simplicity can create stronger brand recognition.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Your Minimalist Logo
          </motion.button>
        </div>
      </section>

      {/* What is Minimalist Design */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            What is Minimalist Logo Design?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Minimalist logo design focuses on simplicity, using only essential elements to communicate brand identity. It removes unnecessary details, colors, and complexity to create clean, memorable designs that work across all applications.
          </motion.p>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground"
          >
            The philosophy behind minimalist design is "less is more." By reducing visual noise, minimalist logos achieve greater impact, better recognition, and longer-lasting relevance in an increasingly crowded marketplace.
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Benefits of Minimalist Logos
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Eye, title: "Memorability", desc: "Simple designs are easier to remember and recognize." },
              { icon: Zap, title: "Versatility", desc: "Works across all sizes and applications seamlessly." },
              { icon: Shield, title: "Timelessness", desc: "Avoids trends and remains relevant for decades." },
              { icon: Target, title: "Scalability", desc: "Maintains quality at any size or resolution." },
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <benefit.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Core Principles of Minimalist Design
          </motion.h2>
          <div className="space-y-4">
            {[
              { principle: "Simplicity", desc: "Remove all non-essential elements. Every element must have a purpose." },
              { principle: "Negative Space", desc: "Use empty space strategically to create visual interest and balance." },
              { principle: "Limited Colors", desc: "Restrict to 1-2 colors for maximum impact and recognition." },
              { principle: "Clean Typography", desc: "Choose simple, readable fonts that complement the design." },
              { principle: "Geometric Shapes", desc: "Use basic shapes for clarity and modern appeal." },
              { principle: "Balance", desc: "Distribute visual weight evenly for harmony and stability." },
            ].map((item, index) => (
              <motion.div
                key={item.principle}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <Layers className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.principle}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Creating Minimalist Logos */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            How to Create a Minimalist Logo
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Start with Concepts", desc: "Brainstorm multiple simple concepts before refining." },
              { step: 2, title: "Simplify Elements", desc: "Reduce each concept to its essential form." },
              { step: 3, title: "Test in Black & White", desc: "Ensure the design works without color." },
              { step: 4, title: "Scale Testing", desc: "Verify clarity at various sizes." },
              { step: 5, title: "Refine and Polish", desc: "Perfect spacing, balance, and proportions." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Common Minimalist Logo Mistakes
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { mistake: "Over-Simplifying", fix: "Don't remove elements that communicate brand identity." },
              { mistake: "Lack of Personality", fix: "Ensure the design still reflects brand character." },
              { mistake: "Poor Differentiation", fix: "Avoid generic shapes that blend with competitors." },
              { mistake: "Ignoring Context", fix: "Consider how the logo will be used in real applications." },
            ].map((item, index) => (
              <motion.div
                key={item.mistake}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2 text-red-400">{item.mistake}</h3>
                <p className="text-muted-foreground">{item.fix}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Famous Minimalist Logo Examples
          </motion.h2>
          <div className="space-y-4">
            {[
              { brand: "Apple", desc: "Simple apple silhouette that represents knowledge and innovation." },
              { brand: "Nike", desc: "Single swoosh conveying movement and speed." },
              { brand: "McDonald's", desc: "Golden arches that are instantly recognizable worldwide." },
              { brand: "Twitter", desc: "Simple bird silhouette representing communication." },
              { brand: "Target", desc: "Clean bullseye representing precision and focus." },
              { brand: "Airbnb", desc: "Simple symbol representing belonging and travel." },
            ].map((item, index) => (
              <motion.div
                key={item.brand}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{item.brand}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* When to Use Minimalist Design */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            When Minimalist Design Works Best
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { use: "Technology Companies", desc: "Modern, innovative brands seeking clean image." },
              { use: "Luxury Brands", desc: "High-end products requiring sophistication." },
              { use: "Professional Services", desc: "Consulting, legal, financial services." },
              { use: "Startups", desc: "New brands needing quick recognition." },
            ].map((item, index) => (
              <motion.div
                key={item.use}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2">{item.use}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Ultimate Guide to Logo Design Services", url: "/logo-design-services-guide", desc: "Comprehensive guide to professional logo design" },
              { title: "What Makes a Good Logo", url: "/what-makes-a-good-logo", desc: "Learn the essential qualities of effective logos" },
              { title: "Types of Logos Explained", url: "/types-of-logos-explained", desc: "Explore different logo types and when to use them" },
              { title: "Logo Design Principles", url: "/logo-design-principles", desc: "Master the fundamental design principles" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Need a Minimalist Logo?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Our designers specialize in creating clean, powerful minimalist logos that build brand recognition and stand the test of time.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default MinimalistLogoDesignGuide;
